/** @module Components */

import React from 'react'
import { Accordion } from 'radix-ui'
import { ChevronDownIcon } from '@heroicons/react/24/solid'
import {
  BuildingOffice2Icon,
  UserGroupIcon,
  RocketLaunchIcon,
  AcademicCapIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline'

interface FAQItem {
  question: string
  answer: string
  icon: React.ElementType
}

const FAQ_ITEMS: FAQItem[] = [
  {
    question: 'What is NASCC?',
    answer:
      'The National Alliance of Sickle Cell Centers (NASCC) is a collaborative network of sickle cell centers across the United States dedicated to improving the lives of individuals affected by sickle cell disease through standardization of care, research collaboration, and advocacy.',
    icon: BuildingOffice2Icon
  },
  {
    question: 'What is our mission?',
    answer:
      'Our mission is to transform sickle cell disease care by establishing standards of care, fostering collaboration among centers, and advocating for improved access to quality care for all individuals living with sickle cell disease.',
    icon: RocketLaunchIcon
  },
  {
    question: 'How do we achieve our goals?',
    answer:
      'We achieve our goals through collaborative research, sharing best practices, developing standardized care protocols, and working closely with healthcare providers, researchers, and community organizations to advance sickle cell disease care and treatment.',
    icon: AcademicCapIcon
  },
  {
    question: 'Who can join NASCC?',
    answer:
      'Healthcare centers that provide comprehensive care for individuals with sickle cell disease can apply to become members. We have specific membership criteria that ensure high standards of care across our network.',
    icon: UserGroupIcon
  }
]

const AccordionItem: React.FC<{
  value: string
  question: string
  answer: string
  icon: React.ElementType
}> = ({ value, question, answer, icon: Icon }) => {
  return (
    <Accordion.Item
      value={value}
      className="mt-4 bg-white rounded-lg shadow-md overflow-hidden"
    >
      <Accordion.Header className="flex w-full">
        <Accordion.Trigger className="group cursor-pointer w-full text-left hover:bg-gray-50 transition-colors">
          <div className="flex w-full items-center justify-between p-4">
            <div className="flex items-center gap-3">
              <Icon className="h-6 w-6 text-[#A6410A]" />
              <h3 className="text-xl font-medium text-[#3C2416]">{question}</h3>
            </div>
            <ChevronDownIcon className="h-5 w-5 transform transition-transform duration-200 ease-in-out group-data-[state=open]:rotate-180" />
          </div>
        </Accordion.Trigger>
      </Accordion.Header>
      <Accordion.Content className="overflow-hidden transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
        <div className="p-4 pt-0">
          <p className="text-[#3C2416]">{answer}</p>
        </div>
      </Accordion.Content>
    </Accordion.Item>
  )
}

export const AboutUs: React.FC = () => {
  return (
    <main className="min-h-screen bg-[#EDE6DE] px-4 py-12 md:px-8 lg:px-16">
      <div className="max-w-[1400px] mx-auto">
        <h1 className="text-4xl md:text-5xl font-serif text-[#3C2416] mb-12">
          About NASCC
        </h1>

        <section className="flex flex-col xl:flex-row gap-8 mb-12">
          <div className="xl:w-1/2">
            <div className="prose prose-lg max-w-none">
              <h2 className="text-xl text-[#A6410A] font-medium mb-4">
                Our Story
              </h2>
              <div className="text-[#3C2416]">
                <p>
                  We are an organization that was formed to provide a foundation
                  to support sickle cell centers in our mission to improve the
                  care of individuals with sickle cell disease (SCD). Our goal
                  is to unify sickle cell centers with our purpose and provide
                  support and infrastructure assistance to increase the number
                  of sickle cell centers available.
                </p>
                <p className="mt-4">
                  Through NASCC we are creating the infrastructure to define a
                  sickle cell center for adults and children and to enhance and
                  promote the adoption of standards of care that improve patient
                  outcomes and quality of life. This includes developing and
                  sharing resources amongst our member centers so they can have
                  access to as much high quality information as possible. We
                  also plan to continuously assess outcomes and improve
                  protocols through the Globin Research Network for Data and
                  Discovery (GRNDaD) registry.
                </p>
                <p className="mt-4">
                  We want to support providers who care for individuals with
                  sickle cell disease, giving them the resources and support
                  they need. Together, we can advocate for the ethical and equal
                  treatment of individuals living with sickle cell disease.
                </p>
                <p className="mt-4 font-extrabold text-[#3C2416]">
                  Our end goal is to make sure that everyone with sickle cell
                  disease has access to a sickle cell specialist to improve
                  their quality of care and quality of life.
                </p>
              </div>
            </div>
          </div>

          <div className="xl:w-1/2">
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl text-[#A6410A] font-medium mb-4">
                Our Mission Statement
              </h2>
              <div className="prose prose-lg max-w-none text-[#3C2416]">
                <blockquote className="border-l-4 border-[#A6410A] pl-4 my-4 italic">
                  To support sickle cell disease centers in providing
                  high-quality comprehensive care by setting standards of care
                  and promoting their adoption, identifying opportunities and
                  resources to strengthen sickle cell disease centers, and
                  advocating for access to comprehensive care to improve health
                  outcomes, quality of life, and survival.
                </blockquote>
              </div>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl text-[#A6410A] font-medium mb-4">
              What does being a NASCC-recognized comprehensive Sickle Cell
              Center mean?
            </h2>
            <div className="prose prose-lg max-w-none text-[#3C2416]">
              <p>
                NASCC recognized Comprehensive Sickle Cell Centers are dedicated
                to improving the outcomes and well-being of people living with
                sickle cell disease. Your NASCC-recognized comprehensive Sickle
                Cell Center will offer BOTH up to date, quality medical care{' '}
                <strong>and</strong> have an experienced, caring staff that
                takes time to develop comprehensive treatment care plans for
                patients and families. A NASCC center also has the ability to
                prescribe all of the available disease-modifying therapies for
                sickle cell disease.
              </p>
              <p className="mt-4">
                Some centers may also have access to research trials - make sure
                you talk to your doctor if you are interested in those trials.
              </p>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl text-[#A6410A] font-medium mb-4">
              Who is part of a NASCC comprehensive Sickle Cell Center?
            </h2>
            <div className="prose prose-lg max-w-none text-[#3C2416]">
              <p>
                Your NASCC comprehensive SCD Center team may include a sickle
                cell specialist, dedicated and trained nursing staff, a social
                worker, and someone to help coordinate your care (this could be
                a nurse, social worker, patient navigator or community health
                worker), among others. Your SCD Center can also help you access
                a primary care physician, an OBGYN, an eye doctor, a
                psychiatrist or psychologist, or other specialty physicians.
              </p>
              <div className="mt-6">
                <h3 className="text-lg font-medium mb-4">
                  Members of a team may include:
                </h3>
                <ul className="space-y-3">
                  {[
                    {
                      title: 'Sickle cell specialist',
                      description: 'specialists in sickle cell disease'
                    },
                    {
                      title: 'Nurses',
                      description: 'medical specialists in sickle cell disease'
                    },
                    {
                      title: 'Social workers',
                      description:
                        'specialists who assist you with the issues of daily living, such as adjusting to living with sickle cell disease and locating resources (e.g. insurance, transportation, housing)'
                    },
                    {
                      title: 'Care coordinator',
                      description:
                        'we know that it can be hard to get all of the care you need. This person will help you coordinate your appointments and medical tests'
                    },
                    {
                      title: 'Patients and families',
                      description:
                        'these folks are an important member of the treatment team. The staff needs your input to develop a plan of care that will ensure you remain healthy, active, and able to live longer and stronger with sickle cell disease'
                    }
                  ].map((item, index) => (
                    <li key={index} className="flex pl-4 items-start">
                      <span className="mr-2 mt-[0.5rem] h-2 w-2 flex-shrink-0 rounded-full bg-[#A6410A]" />
                      <span>
                        <strong>{item.title}:</strong> {item.description}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section>
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="flex justify-between items-center p-6">
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-8 h-8 text-[#A6410A]">
                  <InformationCircleIcon className="w-6 h-6" />
                </div>
                <h2 className="text-xl text-[#A6410A] font-medium">
                  Frequently Asked Questions
                </h2>
              </div>
            </div>

            <div className="px-6 pb-6">
              <Accordion.Root
                type="single"
                collapsible
                className="space-y-4"
                defaultValue="faq-0"
              >
                {FAQ_ITEMS.map((item, index) => (
                  <AccordionItem
                    key={index}
                    value={`faq-${index}`}
                    question={item.question}
                    answer={item.answer}
                    icon={item.icon}
                  />
                ))}
              </Accordion.Root>
            </div>
          </div>
        </section>
      </div>
    </main>
  )
}
